import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import {
  ArrowRight,
  ArrowUpRight,
  HeartPulse,
  Activity,
  Users,
  Stethoscope,
  Building2,
  GraduationCap,
  Church,
  Baby,
  HeartHandshake,
} from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { FAQ } from "../components/FAQ";
import { RevealHeading, Reveal } from "../components/motion";
import { COURSES, AUDIENCES, BENEFITS } from "../data/site";

const heroImg =
  "https://images.unsplash.com/photo-1622115297822-a3798fdbe1f6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxjcHIlMjBpbnN0cnVjdG9yJTIwdHJhaW5pbmclMjBtYW5pa2lufGVufDB8fHx8MTc4NTgwNjUxMnww&ixlib=rb-4.1.0&q=85";

const courseIcons = {
  "cpr-aed-first-aid": HeartPulse,
  bls: Activity,
};

const audienceIcons = {
  Users,
  Stethoscope,
  Building2,
  GraduationCap,
  Church,
  Baby,
  HeartHandshake,
};

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const badgeY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section ref={heroRef} className="relative pt-28 lg:pt-36 pb-16 lg:pb-24" data-testid="hero-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-6">
            <Reveal>
              <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-teal ambient-shadow">
                <span className="h-2 w-2 rounded-full bg-red-accent animate-pulse" />
                Missouri CPR &amp; First Aid Training
              </span>
            </Reveal>

            <h1 className="mt-7 font-editorial font-black text-navy tracking-tight leading-[0.95] text-5xl sm:text-6xl lg:text-7xl">
              <RevealHeading lines={["Learn CPR.", "Save a Life."]} testId="hero-heading" delay={0.15} />
            </h1>

            <Reveal delay={0.5}>
              <p className="mt-7 max-w-xl font-body text-lg leading-relaxed text-slate-600">
                Hands-on CPR, AED, First Aid, and BLS training for individuals,
                healthcare professionals, businesses, and organizations
                throughout Missouri.
              </p>
            </Reveal>

            <Reveal delay={0.62}>
              <div className="mt-9 flex flex-col sm:flex-row gap-4">
                <Link
                  to="/courses#contact"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-red-accent px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#be123c] hover:-translate-y-0.5 ambient-shadow"
                  data-testid="hero-book-class-btn"
                >
                  Book a Class
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/courses"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-navy/15 bg-white px-8 py-4 text-base font-semibold text-navy transition-all duration-300 hover:bg-navy hover:text-white hover:-translate-y-0.5"
                  data-testid="hero-view-courses-btn"
                >
                  View Our Courses
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.25 }}
              className="relative"
            >
              <div className="relative overflow-hidden rounded-[2rem] ambient-shadow-lg aspect-[4/5] sm:aspect-[5/5] bg-slate-200">
                <motion.img
                  src={heroImg}
                  alt="Professional CPR instructor teaching CPR with a training manikin"
                  style={{ y: imgY }}
                  className="absolute inset-0 h-[122%] w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy/35 via-transparent to-transparent" />
              </div>

              <motion.div
                style={{ y: badgeY }}
                className="absolute -bottom-6 -left-4 sm:left-6 rounded-3xl bg-white p-5 ambient-shadow-lg border border-slate-100 max-w-[15rem]"
              >
                <div className="flex items-center gap-3">
                  <span className="grid place-items-center h-11 w-11 rounded-2xl bg-red-accent text-white">
                    <HeartPulse className="h-5 w-5" />
                  </span>
                  <div>
                    <p className="font-grotesk font-extrabold text-navy text-lg leading-none">
                      Every Second
                    </p>
                    <p className="font-body text-sm text-slate-500 mt-1">
                      counts. Be ready.
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section className="py-4 bg-[#D1FAE5] border-y border-emerald-200/60" data-testid="marquee-section">
        <Marquee speed={40} gradient={false} autoFill>
          {[
            "Empowering Missouri",
            "Hands-on Training",
            "CPR • AED • First Aid • BLS",
            "Confidence When It Counts",
          ].map((t, i) => (
            <span key={i} className="mx-12 inline-flex items-center gap-12">
              <span className="font-editorial italic text-2xl md:text-3xl text-teal">
                {t}
              </span>
              <HeartPulse className="h-6 w-6 text-red-accent" />
            </span>
          ))}
        </Marquee>
      </section>

      {/* ABOUT */}
      <section className="py-24 lg:py-32" data-testid="about-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-4">
            <Reveal>
              <span className="font-grotesk text-sm uppercase tracking-[0.2em] text-teal">
                About Us
              </span>
            </Reveal>
          </div>
          <div className="lg:col-span-8">
            <Reveal>
              <h2 className="font-editorial font-black text-navy tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
                Training That Builds Confidence
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 max-w-2xl font-body text-xl leading-relaxed text-slate-600">
                Second Chance CPR provides practical, hands-on emergency
                response training in Missouri. Our goal is to help individuals
                and organizations feel prepared, confident, and ready to respond
                when every second counts.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-teal ambient-shadow" data-testid="hsi-badge">
                  <span className="h-2 w-2 rounded-full bg-red-accent" />
                  HSI Authorized Instructor
                </span>
                <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.2em] text-navy">
                  Certification Materials Issued
                </span>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COURSES PREVIEW */}
      <section className="py-8 lg:pb-28" data-testid="courses-preview-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <Reveal>
              <div>
                <span className="font-grotesk text-sm uppercase tracking-[0.2em] text-teal">
                  Courses Offered
                </span>
                <h2 className="mt-4 font-editorial font-black text-navy tracking-tight text-4xl sm:text-5xl">
                  Skills for every situation
                </h2>
              </div>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {COURSES.map((c, i) => {
              const Icon = courseIcons[c.id];
              return (
                <Reveal key={c.id} delay={i * 0.08}>
                  <div className="group h-full flex flex-col rounded-3xl bg-white border border-slate-200 p-8 sm:p-10 ambient-shadow transition-all duration-300 hover:-translate-y-1.5 hover:ambient-shadow-lg" data-testid={`course-card-${c.id}`}>
                    <span className="grid place-items-center h-16 w-16 rounded-2xl bg-red-accent/10 text-red-accent">
                      <Icon className="h-8 w-8" strokeWidth={1.75} />
                    </span>
                    <h3 className="mt-6 font-grotesk font-extrabold text-navy text-2xl sm:text-3xl leading-tight">
                      {c.title}
                    </h3>
                    <p className="mt-4 font-body text-base leading-relaxed text-slate-600 flex-1">
                      {c.short}
                    </p>
                    <Link
                      to={`/courses#${c.id}`}
                      className="mt-6 inline-flex items-center gap-2 font-semibold text-teal transition-all group-hover:gap-3"
                      data-testid={`course-learn-more-${c.id}`}
                    >
                      Learn More
                      <ArrowUpRight className="h-4 w-4" />
                    </Link>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE */}
      <section className="py-24 lg:py-28 bg-navy" data-testid="who-we-serve-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <span className="font-grotesk text-sm uppercase tracking-[0.2em] text-[#5eead4]">
              Who We Serve
            </span>
            <h2 className="mt-4 max-w-3xl font-editorial font-black text-white tracking-tight leading-[1.05] text-4xl sm:text-5xl">
              Training built for your people
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {AUDIENCES.map((a, i) => {
              const Icon = audienceIcons[a.icon];
              return (
                <Reveal key={a.label} delay={i * 0.06}>
                  <div className="group h-full rounded-3xl border border-white/10 bg-white/5 p-7 transition-all duration-300 hover:bg-white/10 hover:-translate-y-1" data-testid={`audience-${i}`}>
                    <span className="grid place-items-center h-12 w-12 rounded-2xl bg-[#0D9488] text-white">
                      <Icon className="h-6 w-6" strokeWidth={1.75} />
                    </span>
                    <p className="mt-5 font-grotesk font-bold text-white text-lg leading-snug">
                      {a.label}
                    </p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US - manifesto */}
      <section className="py-24 lg:py-32" data-testid="why-choose-us-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <span className="font-grotesk text-sm uppercase tracking-[0.2em] text-teal">
              Why Choose Us
            </span>
            <h2 className="mt-4 max-w-3xl font-editorial font-black text-navy tracking-tight leading-[1.03] text-4xl sm:text-5xl lg:text-6xl">
              A better way to get prepared
            </h2>
          </Reveal>

          <div className="mt-16 grid gap-x-14 gap-y-14 md:grid-cols-2 lg:grid-cols-3">
            {BENEFITS.map((b, i) => (
              <Reveal key={b.n} delay={(i % 3) * 0.08}>
                <div className="border-t border-slate-200 pt-6" data-testid={`benefit-${b.n}`}>
                  <span className="font-editorial text-5xl font-black text-red-accent/90">
                    {b.n}
                  </span>
                  <h3 className="mt-4 font-grotesk font-extrabold text-navy text-2xl">
                    {b.title}
                  </h3>
                  <p className="mt-3 font-body text-slate-600 leading-relaxed">
                    {b.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* CTA */}
      <section className="pb-24 lg:pb-32" data-testid="cta-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-teal px-8 py-16 sm:px-16 sm:py-20 ambient-shadow-lg">
              <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10" />
              <div className="absolute -left-10 -bottom-20 h-72 w-72 rounded-full bg-[#0f766e]/40" />
              <div className="relative max-w-3xl">
                <h2 className="font-editorial font-black text-white tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
                  Be Prepared When Every Second Counts
                </h2>
                <p className="mt-6 font-body text-xl text-emerald-50/90 leading-relaxed">
                  Get the training and confidence you need to respond during an
                  emergency.
                </p>
                <Link
                  to="/courses#contact"
                  className="mt-9 inline-flex items-center gap-2 rounded-full bg-red-accent px-8 py-4 text-base font-semibold text-white transition-all duration-300 hover:bg-[#be123c] hover:-translate-y-0.5 ambient-shadow"
                  data-testid="cta-request-class-btn"
                >
                  Request a Class
                  <ArrowRight className="h-5 w-5" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
